import React, { useEffect, useState } from "react";
import { FaPlus, FaSave, FaEdit, FaTrash } from "react-icons/fa";
import Dialog from "../FormDialog/Dialog";

import ResourceDropdown from '../../DropDowns/PI/PIResourceDropdown';
import MetricDropdown from '../../DropDowns/PI/PIMetricDropdown';
import PIElementDropdown from '../../DropDowns/PI/PIElementDropdown';
import AttributeDropdown from '../../DropDowns/PI/PIAttributeDropdown';
import SummaryTypeDropdown from '../../DropDowns/PI/PISummaryDropdown';
import EnabledDropdown from '../../DropDowns/EnabledDropdown/EnabledDropdown';

import apiClientTimeseries from "../../../services/apiClientTimeseries";
import { getHeaders as getMetricHeaders } from "../../ApiHeaders/apiHeadersTimeseries";
import { FaSyncAlt } from "react-icons/fa";
import { FaCopy } from "react-icons/fa";




type FormMode = "create" | "edit" | "view" | null;

interface PIScreenFormProps {
  records: any[];
  selectedRecord?: any;
  assets: any[];
  metrics: any[];
  isCreateMode: boolean;  
  user: { fullName: string; email: string } | null;
  onCreate: (payload: any) => void;
  onUpdate: (piMetricId: number, payload: any) => void;
  onDelete: (piMetricId: number) => void;
  onDirtyChange: (dirty: boolean) => void;
  onModeChange: (mode: FormMode) => void;
  canDelete?: boolean; 
}


const PIScreenForm: React.FC<PIScreenFormProps> = ({
  selectedRecord,
  assets,
  metrics,
  isCreateMode,   
  user,
  onCreate,
  onUpdate,
  onDelete,
  onDirtyChange,
  onModeChange,
  canDelete = false

}) => {

  const [resource, setResource] = useState("");
  const [metric, setMetric] = useState("");
  const [piElement, setPIElement] = useState("");
  const [attribute, setAttribute] = useState("");
  const [latestValue, setLatestValue] = useState("");
  const [webId, setWebId] = useState("");
  const [summaryType, setSummaryType] = useState("");
  const [active, setActive] = useState("Y");

  const [piMetricId, setPIMetricId] = useState<number | null>(null);

  const [isViewMode, setIsViewMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [mode, setMode] = useState<FormMode>(null);

  const [activeField, setActiveField] = useState<string | null>(null);

  const [piElementPath, setPiElementPath] = useState<string | null>(null);

  const [betmObjectId, setBetmObjectId] = useState<number | null>(null);

  const [dataPointName, setDataPointName] = useState("");



const [modifiedFields, setModifiedFields] = useState<Record<string, boolean>>({
  resource: false,
  metric: false,
  active: false,
});

const normalize = (v?: string) => v?.trim().toUpperCase() ?? "";

const FUEL_ALIAS_MAP: Record<string, string> = {
  "ENERGY STORAGE": "BATTERY",
};

const normalizeFuel = (v?: string) => {
  const key = normalize(v);
  return FUEL_ALIAS_MAP[key] ?? key;
};

const resourceNameToAssetMap = React.useMemo(() => {
  const map = new Map<string, any>();

  assets.forEach((asset) => {
    asset.resources?.forEach((r: any) => {
      map.set(r.iso_resource_name, asset);
    });
  });

  return map;
}, [assets]);

const resourceOptions = React.useMemo(
  () =>
    Array.from(resourceNameToAssetMap.keys()).map((name) => ({
      value: name,
      label: name,
    })),
  [resourceNameToAssetMap]
);

const [filteredMetrics, setFilteredMetrics] = useState<any[]>([]);


const parsePiEndValue = (endValue: any) => {
  if (endValue == null) return "";

  // Already numeric or simple string
  if (typeof endValue === "number") return endValue.toString();
  if (typeof endValue === "string" && !endValue.trim().startsWith("{"))
    return endValue;

  // EnumerationValue → extract Value
  try {
    const normalized = endValue
      .replace(/'/g, '"')        // Python dict → JSON
      .replace("False", "false")
      .replace("True", "true");

    const obj = JSON.parse(normalized);

    return obj.Value?.toString() ?? "";
  } catch {
    return "";
  }
};


useEffect(() => {
  // If parent says we are in create mode AND no row is selected,
  // the form must enter create state.
  if (isCreateMode && !selectedRecord) {
    resetForm();
    setMode("create");
    setIsViewMode(false);
    setIsEditMode(false);
    onModeChange("create");
  }
}, [isCreateMode, selectedRecord]);


const handleResourceChange = (resourceName: string) => {
  setResource(resourceName);
  onDirtyChange(true);

  // Only filter metrics when creating
  if (!isCreateMode) return;

  const asset = resourceNameToAssetMap.get(resourceName);
  if (!asset) {
    setFilteredMetrics([]);
    setMetric("");
    return;
  }

  const assetIso = normalize(asset.iso_code);
  const assetFuel = normalizeFuel(asset.generation_subtype_code);

  const matchedMetrics = metrics.filter((m: any) => {
    const metricIso = normalize(m.iso_code);
    const metricFuel = normalizeFuel(m.fuel_type);

    return (
      (metricIso === assetIso && metricFuel === assetFuel) ||
      (metricIso === "ALL" && metricFuel === assetFuel) ||
      (metricFuel === "ALL" && metricIso === assetIso) ||
      (metricIso === "ALL" && metricFuel === "ALL")
    );
  });

  setFilteredMetrics(matchedMetrics);
  setMetric("");
};




const refreshLatestValue = async () => {
  if (!webId) return;

  try {
    const data = await fetchEndValueByWebId(webId);
    if (!data?.status) return;

    const endValue = data.value?.Value ?? data.value;
    setLatestValue(endValue?.toString() ?? "");

    // Only auto-set summary type while creating
    if (mode === "create") {
      setSummaryType(inferSummaryType(endValue));
    }
  } catch (err) {
    console.error("Failed to refresh latest value:", err);
  }
};


useEffect(() => {
  if (!webId || mode === "create") return;

  const timer = setTimeout(() => {
    refreshLatestValue();
  }, 5000);

  return () => clearTimeout(timer);
}, [webId, mode]);

const getFinalDataPointName = () => {
  if (mode === "create") {
    if (!piElement || !attribute) return "";
    return `${piElement}.${attribute}`;
  }

  return dataPointName;
};




const [copied, setCopied] = useState(false);

const copyWebIdToClipboard = async () => {
  if (!webId) return;
  await navigator.clipboard.writeText(webId);
  setCopied(true);
  setTimeout(() => setCopied(false), 1500);
};

const inferSummaryType = (endValue: any) => {
  if (!endValue) return "";

  if (typeof endValue === "string" && endValue.trim().startsWith("{")) {
    return "INTERPOLATED";
  }

  return "SUMMARIZED";
};




  const [dialog, setDialog] = useState<any>({
    open: false,
    title: "",
    message: "",
    confirmButtonText: "",
    cancelButtonText: "",
    onConfirm: () => {},
    onCancel: () => {},
  });

  const closeDialog = () => {
    setDialog({
      open: false,
      title: "",
      message: "",
      confirmButtonText: "",
      cancelButtonText: "",
      onConfirm: () => {},
      onCancel: () => {},
    });
  };

  useEffect(() => {
    if (!selectedRecord) {
      resetForm();
      return;
    }
  
    setMode("view");
    setIsEditMode(false);
    setIsViewMode(true);
  
    setResource(selectedRecord.ISO_RESOURCE_NAME || "");
    setMetric(selectedRecord.METRIC_NAME || "");
    setDataPointName(selectedRecord.DATA_POINT_NAME || "");



    setSummaryType(selectedRecord.SUMMARY_TYPE || "");
    setActive(selectedRecord.ACTIVE || "Y");
    setWebId(selectedRecord.PI_WEBID || "");
    setLatestValue("");
  
    setPIMetricId(selectedRecord.PI_METRIC_ID);      //  required
    setBetmObjectId(selectedRecord.BETM_OBJECT_ID);  //  required
  
    setFilteredMetrics([
      {
        metric_name: selectedRecord.METRIC_NAME,
        METRIC_NAME: selectedRecord.METRIC_NAME,
        metric_internal_key: selectedRecord.METRIC_INTERNAL_KEY,
      },
    ]);
  
    onDirtyChange(false);
    onModeChange("view");
  }, [selectedRecord]);
  
  

  const resetForm = () => {
    setResource("");
    setMetric("");
    setPIElement("");
    setAttribute("");
    setLatestValue("");
    setWebId("");
    setSummaryType("");
    setActive("Y");
    setPIMetricId(null);

    setIsViewMode(false);
    setIsEditMode(false);
    setMode(null);

    onDirtyChange(false);
    // onModeChange(null);
  };

  

  const buildDataPointName = () => {
    if (!piElement || !attribute) return "";
    return `${piElement}.${attribute}`;
  };
  

  const handleCreate = () => {
    const userValue = user?.email || user?.fullName || "Unknown User";
  
    const selectedMetricObj = filteredMetrics.find(
      (m: any) => (m.metric_name ?? m.METRIC_NAME) === metric
    );
  
    if (!selectedMetricObj) {
      alert("Please select a valid Metric");
      return;
    }
  
    const payload = {
      BETM_OBJECT_ID: 0,
      ISO_RESOURCE_NAME: resource,
      DATA_POINT_NAME: getFinalDataPointName(),
      PI_REFINED_CATEGORY: selectedMetricObj.metric_internal_key,
      PI_WEBID: webId,
      SUMMARY_TYPE: summaryType,
      SYS_CREATED_BY: userValue,
      SYS_MODIFIED_BY: userValue,
    };
  
    setDialog({
      open: true,
      title: "Confirm Create",
      message: "Create this PI Metric Resource?",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      onConfirm: () => {
        onCreate(payload);
        closeDialog();
      },
      onCancel: closeDialog,
    });
  };

  const handleSave = () => {
    if (!piMetricId || !betmObjectId) return;
  
    const userValue = user?.email || user?.fullName || "Unknown User";

const selectedMetricObj = filteredMetrics.find(
  (m: any) => (m.metric_name ?? m.METRIC_NAME) === metric
);

if (!selectedMetricObj || !piMetricId || !betmObjectId) {
  alert("Missing required data");
  return;
}

const payload = {
  PI_METRIC_ID: piMetricId,
  BETM_OBJECT_ID: betmObjectId,
  DATA_POINT_NAME: getFinalDataPointName(),  
  PI_REFINED_CATEGORY: selectedMetricObj.metric_internal_key,
  PI_WEBID: webId,
  SUMMARY_TYPE: summaryType,
  ACTIVE: active,
  SYS_MODIFIED_BY: userValue,
};

  
    setDialog({
      open: true,
      title: "Confirm Edit",
      message: "Save changes to this PI Metric Resource?",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      onConfirm: () => {
        onUpdate(piMetricId, payload);
        closeDialog();
      },
      onCancel: closeDialog,
    });
  };
  
  
  const handleEdit = () => {
    setIsEditMode(true);
    setIsViewMode(false);
    setMode("edit");
    onModeChange("edit");
  };

  const handleNew = () => {
    resetForm();
    setMode("create");
    onModeChange("create");
  
    // Tell parent to clear row selection
    // This is what actually unlocks the form
    window.dispatchEvent(new CustomEvent("pi-clear-selection"));
  };
  

  const handleDeleteClick = () => {
    if (!piMetricId) return;

    setDialog({
      open: true,
      title: "Confirm Delete",
      message: "Are you sure you want to delete this PI Metric Resource?",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      onConfirm: () => {
        onDelete(piMetricId);
        closeDialog();
      },
      onCancel: closeDialog,
    });
  };


  const handleCancel = () => {
    if (mode === "create") {
      resetForm();
    }
  
    if (mode === "edit" && selectedRecord) {
      // restore selected record
      setResource(selectedRecord.ISO_RESOURCE_NAME || "");
      setMetric(selectedRecord.METRIC_NAME || "");
      setPIElement(selectedRecord.DATA_POINT_NAME || "");
      // setAttribute(selectedRecord.METRIC_INTERNAL_KEY || "");
      setLatestValue("");
      setWebId(selectedRecord.PI_WEBID || "");
      setSummaryType(selectedRecord.SUMMARY_TYPE || "");
      setActive(selectedRecord.ACTIVE || "Y");
    }
  
    setIsEditMode(false);
    setIsViewMode(true);
    setMode("view");
    onDirtyChange(false);
  };
  

  const fetchEndValueByWebId = async (webId: string) => {
    try {
      const headers = await getMetricHeaders();
  
      const res = await apiClientTimeseries.post(
        "/pi/end_value",
        { web_id: webId },
        { headers }
      );
  
      return res?.data;
    } catch (err) {
      console.error("Failed to fetch end value", err);
      return null;
    }
  };
  
  
  

  const disabled = isViewMode && !isEditMode;

  useEffect(() => {
    // When parent switches to Create mode and no record is selected,
    // force a full form reset
    if (isCreateMode && !selectedRecord) {
      resetForm();
      setMode("create");
      setIsViewMode(false);
      setIsEditMode(false);
      onModeChange("create");
    }
  }, [isCreateMode, selectedRecord]);
  
  useEffect(() => {
    // If parent exits create mode AND a record is selected,
    // force the form into VIEW mode
    if (!isCreateMode && selectedRecord) {
      setIsEditMode(false);
      setIsViewMode(true);
      setMode("view");
    }
  }, [isCreateMode, selectedRecord]);
  

  return (
    <div className="bg-white p-6 rounded-lg shadow-md font-poppins">
      {dialog.open && <Dialog {...dialog} onClose={closeDialog} />}

      {/* BUTTONS */}
      <div className="flex justify-between items-center gap-4 mb-4">
        <div>
          
          {isEditMode && canDelete && (
            <button
              onClick={(e) => {
                e.preventDefault();
                handleDeleteClick();
              }}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600"
              type="button"
            >
              <FaTrash className="w-4 h-4" /> Delete
            </button>
          )}
        </div>

        <div className="flex items-center gap-4">
          {!isViewMode && !isEditMode && (
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747]"
            >
              <FaPlus className="w-4 h-4" /> Create
            </button>
          )}

          {isViewMode && !isEditMode && (
            <>
              <button
                onClick={handleNew}
                className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747]"
              >
                <FaPlus className="w-4 h-4" /> New
              </button>

              <button
                onClick={handleEdit}
                className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747]"
              >
                <FaEdit className="w-4 h-4" /> Edit
              </button>
            </>
          )}

{isEditMode && (
  <>
    <button
      onClick={handleSave}
      className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white rounded-lg hover:bg-[#474747]"
    >
      <FaSave /> Save
    </button>

    <button
      type="button"
      onClick={handleCancel}
      className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
    >
      Cancel
    </button>
  </>
)}

        </div>
      </div>

      <form className="space-y-6">
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
          {/* RESOURCE */}
          <div>
            <label className="block text-gray-600 mb-1">
              Resource<span className="text-red-500 text-lg align-top ml-1">*</span>
            </label>

            <ResourceDropdown
              selectedResource={resource}
              resources={assets}
              onResourceChange={handleResourceChange}
              
              onFocus={() => setActiveField("resource")}
              disabled={selectedRecord && !isEditMode}
              className={`w-full p-1 mt-[-4px] border rounded-lg shadow-inner text-sm transition-all duration-200
                ${
                  !isEditMode
                    ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                    : isEditMode && (activeField === "resource" || modifiedFields.resource)
                      ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                      : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                }`}
            />
          </div>

          {/* METRIC */}
          <div>
            <label className="block text-gray-600 mb-1">
              Metric<span className="text-red-500 text-lg align-top ml-1">*</span>
            </label>

            <MetricDropdown
              selectedMetric={metric}
              metrics={filteredMetrics}
              onMetricChange={(value) => {
                setMetric(value);
                onDirtyChange(true);
              }}
              onFocus={() => setActiveField("metric")}
              disabled={selectedRecord && !isEditMode}
              className={`w-full p-1 mt-[-4px] border rounded-lg shadow-inner text-sm transition-all duration-200
                ${
                  !isEditMode
                    ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                    : isEditMode && (activeField === "metric" || modifiedFields.metric)
                      ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                      : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                }`}
            />
          </div>
        </div>

<div className="flex gap-4 mt-4">

{mode === "create" && (
  <>
    {/* PI ELEMENT */}
    <div className="flex flex-col flex-1">
      <label className="block text-gray-600 mb-1">
        PI Element<span className="text-red-500 text-lg align-top ml-1">*</span>
      </label>

      <PIElementDropdown
        selectedPIElement={piElement}
        onPIElementChange={(value, obj) => {
          setPIElement(value);
          setPiElementPath(obj?.path || null);
          setAttribute("");           // reset attribute when element changes
          onDirtyChange(true);
        }}
        disabled={false}
        className={`w-full p-1 mt-[-4px] border rounded-lg shadow-inner text-sm bg-white
        ${
          activeField === "piElement"
            ? "border-blue-500 focus:ring-2 focus:ring-blue-500"
            : "border-gray-300"
        }`}
            />
    </div>

    {/* ATTRIBUTE */}
    <div className="flex flex-col flex-1">
      <label className="block text-gray-600 mb-1">
        Attribute<span className="text-red-500 text-lg align-top ml-1">*</span>
      </label>

      <AttributeDropdown
        piElementPath={piElementPath}
        selectedAttribute={attribute}
        onAttributeChange={(value, obj) => {
          setAttribute(value);
          setLatestValue(parsePiEndValue(obj?.end_value));
          setWebId(obj?.web_id ?? "");

          //  Auto-infer summary type only in CREATE
          setSummaryType(inferSummaryType(obj?.end_value));

          onDirtyChange(true);
        }}
        disabled={!piElementPath}
        className={`w-full p-1 mt-[-4px] border rounded-lg shadow-inner text-sm bg-white
        ${
          activeField === "attribute"
            ? "border-blue-500 focus:ring-2 focus:ring-blue-500"
            : "border-gray-300"
        }`}
      
      />
    </div>
  </>
)}

{mode !== "create" && (
  <div className="flex flex-col basis-1/2 max-w-1/2 pr-2">
    <label className="block text-gray-600 mb-1">
      Data Point Name<span className="text-red-500 text-lg align-top ml-1">*</span>
    </label>

    <input
      type="text"
      value={dataPointName}
      onChange={(e) => {
        setDataPointName(e.target.value);
        onDirtyChange(true);
      }}
      disabled={isViewMode}
      className={`w-full p-3 border rounded-lg shadow-inner text-sm transition
        ${
          isViewMode
            ? "bg-[#f9f9f9] text-gray-700 border-gray-300 cursor-not-allowed"
            : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
        }`}
    />
  </div>
)}

</div>


        <div className="flex gap-4">
        <div className="flex flex-col flex-1">
  <label className="block text-gray-600 mb-1">
    Latest Value
  </label>

  <div className="flex items-center gap-2">
    <input
      type="text"
      value={latestValue}
      readOnly
      placeholder="Auto-filled from attribute"
      className="flex-1 p-3 border rounded-lg bg-[#f9f9f9] text-gray-800 shadow-inner text-sm border-gray-300 cursor-not-allowed"
    />

    <button
  type="button"
  onClick={refreshLatestValue}
  disabled={!webId || mode === "create"}
  title="Refresh latest value"
  className={`p-2 rounded-lg border transition ${
    !webId || mode === "create"
      ? "bg-gray-200 cursor-not-allowed"
      : "bg-white hover:bg-gray-100 border-gray-300"
  }`}
>
  <FaSyncAlt className="text-gray-600 text-sm" />
</button>

  </div>
</div>


        </div>

        <div className="flex gap-4">
        <div className="flex flex-col flex-1">
  <label className="block text-gray-600 mb-1">
    Web ID
  </label>

  <div className="flex items-center gap-2">
    <input
      type="text"
      value={webId}
      readOnly
      placeholder="Auto-filled from attribute"
      className="flex-1 p-3 border rounded-lg bg-[#f9f9f9] text-gray-800 shadow-inner text-sm border-gray-300 cursor-not-allowed"
    />

    <button
      type="button"
      onClick={copyWebIdToClipboard}
      disabled={!webId}
      title={copied ? "Copied!" : "Copy Web ID"}
      className={`p-2 rounded-lg border transition ${
        !webId
          ? "bg-gray-200 cursor-not-allowed"
          : "bg-white hover:bg-gray-100 border-gray-300"
      }`}
    >
<FaCopy className={copied ? "text-green-600" : "text-gray-600"} />
    </button>
  </div>
</div>


        </div>

        <div className="flex gap-4">
          <div className="flex flex-col w-1/2">
    <label className="block text-gray-600 mb-1">
      Summary Type<span className="text-red-500 text-lg align-top ml-1">*</span>
    </label>

    <SummaryTypeDropdown
      selectedSummaryType={summaryType}
      onSummaryTypeChange={(value) => {
        setSummaryType(value);
        onDirtyChange(true);
        setModifiedFields((prev) => ({ ...prev, summaryType: true }));
      }}
      onFocus={() => setActiveField("summaryType")}
      disabled={selectedRecord && !isEditMode}
      className={`w-full p-1 mt-[-4px] border rounded-lg shadow-inner text-sm transition-all duration-200
        ${
          !isEditMode
            ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
            : isEditMode &&
              (activeField === "summaryType" || modifiedFields.summaryType)
            ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
            : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
        }`}
    />
  </div>

  <div className="flex flex-col w-1/2">
    <label className="block text-gray-600 mb-1">
      Enabled<span className="text-red-500 text-lg align-top ml-1">*</span>
    </label>

    <EnabledDropdown
      selectedEnabled={active}
      onEnabledChange={(value) => {
        setActive(value);
        onDirtyChange(true);
        setModifiedFields((prev) => ({ ...prev, active: true }));
      }}
      onFocus={() => setActiveField("active")}
      disabled={selectedRecord && !isEditMode}
      className={`w-full p-1 mt-[-4px] border rounded-lg shadow-inner text-sm transition-all duration-200
        ${
          !isEditMode
            ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
            : isEditMode && (activeField === "active" || modifiedFields.active)
            ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
            : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
        }`}
    />
  </div>
</div>


      </form>
    </div>
  );
};



export default PIScreenForm;
